import React, { useEffect, useState } from "react";
import { generateWebPushKeys, getWebPushKeys } from "../../api/apiServiceFetch";

// Совпадает со значением по умолчанию на сервере (WebPushSettings.DefaultSubject).
const DEFAULT_SUBJECT = "mailto:info@jerm.am";

/**
 * Ключи VAPID для push-уведомлений в браузере. Без них канал не работает вовсе:
 * веб-клиент запрашивает публичный ключ у сервера и без него не подписывается.
 *
 * Приватная половина пары пишется в базу и наружу не отдаётся — здесь её не
 * видно и запросить её нельзя. Публичный ключ не секрет, его и так получает
 * каждый браузер.
 */
const WebPushKeysCard = () => {
	const [keys, setKeys] = useState({ configured: false, publicKey: "", subject: "" });
	const [subject, setSubject] = useState(DEFAULT_SUBJECT);
	const [isLoading, setIsLoading] = useState(true);
	const [isGenerating, setIsGenerating] = useState(false);
	const [isConfirming, setIsConfirming] = useState(false);
	const [status, setStatus] = useState(null); // { ok: bool, text: string }

	useEffect(() => {
		(async () => {
			try {
				const data = await getWebPushKeys();
				setKeys({
					configured: Boolean(data?.configured),
					publicKey: data?.publicKey || "",
					subject: data?.subject || "",
				});
				setSubject(data?.subject || DEFAULT_SUBJECT);
			} catch (error) {
				console.error("Load web push keys error:", error);
				setStatus({ ok: false, text: "Не удалось загрузить состояние ключей" });
			} finally {
				setIsLoading(false);
			}
		})();
	}, []);

	const handleGenerate = async () => {
		setIsGenerating(true);
		setIsConfirming(false);
		setStatus(null);
		try {
			const data = await generateWebPushKeys(subject.trim());
			setKeys({
				configured: Boolean(data?.configured),
				publicKey: data?.publicKey || "",
				subject: data?.subject || "",
			});
			setSubject(data?.subject || DEFAULT_SUBJECT);
			setStatus({ ok: true, text: "Новая пара ключей создана и сохранена." });
		} catch (error) {
			console.error("Generate web push keys error:", error);
			setStatus({
				ok: false,
				text: `Не удалось создать ключи: ${
					error?.message || "запрос не удался"
				}. Проверьте контакт: допустим mailto: или https://`,
			});
		} finally {
			setIsGenerating(false);
		}
	};

	// Первая пара ничего не ломает — её создаём одним нажатием. А вот
	// перегенерация уже настроенных ключей отключает всех подписчиков, поэтому
	// она идёт через явное подтверждение с описанием последствий.
	const handleGenerateClick = () => {
		if (keys.configured) {
			setStatus(null);
			setIsConfirming(true);
			return;
		}
		handleGenerate();
	};

	return (
		<section className="settings-page__card">
			<h3>Push-уведомления в браузере</h3>
			<p className="settings-page__hint">
				Чтобы сайт мог присылать уведомления, в базе должна лежать пара ключей
				VAPID. Пока её нет, браузеры не могут подписаться и уведомления не
				приходят вовсе. Приватный ключ хранится на сервере и никуда не
				показывается — ниже виден только публичный, он и так уходит в каждый
				браузер.
			</p>

			<div className="settings-page__row">
				<label className="settings-page__label">Состояние</label>
				<span className="settings-page__hint settings-page__hint--inline">
					{isLoading
						? "Загрузка..."
						: keys.configured
							? "Ключи настроены"
							: "Ключи не настроены"}
				</span>
			</div>

			{!isLoading && keys.configured && (
				<>
					<div className="settings-page__row">
						<label className="settings-page__label">Публичный ключ</label>
					</div>
					<pre className="settings-page__answer">{keys.publicKey}</pre>

					<div className="settings-page__row">
						<label className="settings-page__label">Контакт сейчас</label>
						<span className="settings-page__hint settings-page__hint--inline">
							{keys.subject || DEFAULT_SUBJECT}
						</span>
					</div>
				</>
			)}

			<div className="settings-page__row">
				<label className="settings-page__label">Контакт оператора</label>
				<input
					type="text"
					value={subject}
					placeholder={DEFAULT_SUBJECT}
					disabled={isLoading || isGenerating}
					onChange={(e) => {
						setSubject(e.target.value);
						setStatus(null);
						setIsConfirming(false);
					}}
				/>
			</div>

			<p className="settings-page__hint">
				Этот адрес push-сервисы (Google, Apple, Mozilla) видят как контакт
				отправителя и пишут на него, если с уведомлениями что-то не так.
				Допустимы «mailto:почта» и «https://адрес». Пустое поле означает{" "}
				{DEFAULT_SUBJECT}.
			</p>

			<div className="settings-page__row">
				<button
					type="button"
					disabled={isLoading || isGenerating || isConfirming}
					onClick={handleGenerateClick}
				>
					{isGenerating
						? "Создание..."
						: keys.configured
							? "Создать новые ключи"
							: "Создать ключи"}
				</button>

				{!isLoading && !keys.configured && !isGenerating && (
					<p className="settings-page__hint settings-page__hint--beside">
						Ключей ещё нет — создание ничего не ломает.
					</p>
				)}
			</div>

			{isConfirming && (
				<div className="settings-page__confirm">
					<p className="settings-page__confirm-text">
						Новые ключи отключат уведомления у всех, кто уже подписался в
						браузере. Старые подписки подписаны прежним ключом, и push-сервисы
						начнут их отклонять — эти люди перестанут получать уведомления.
						Подписка восстановится сама при следующем заходе человека на сайт,
						но до этого момента уведомления до него доходить не будут.
					</p>
					<p className="settings-page__confirm-text">
						Создавать новую пару стоит, только если ключи потеряны или
						скомпрометированы. Если уведомления уже работают — не трогайте.
					</p>
					<div className="settings-page__row">
						<button type="button" onClick={handleGenerate}>
							Да, создать новые ключи и отключить текущие подписки
						</button>
						<button
							type="button"
							className="settings-page__button--secondary"
							onClick={() => setIsConfirming(false)}
						>
							Отмена
						</button>
					</div>
				</div>
			)}

			{status && (
				<p
					className={`settings-page__status ${
						status.ok ? "is-ok" : "is-error"
					}`}
				>
					{status.text}
				</p>
			)}
		</section>
	);
};

export default WebPushKeysCard;
